import { useState } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db8fkcbk13ra8mig8qs0";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("感谢您的留言，我们已收到您的反馈，会尽快通过邮件与您联系。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = typeof value === "string" ? value.trim() : "";
      if (!text) return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setFeedback("提交成功！感谢您的留言，我们会尽快通过邮件与您联系。");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(
          typeof serverMsg === "string" && serverMsg
            ? `提交失败：${serverMsg}`
            : "提交失败，请稍后重试，或直接发送邮件至 contact@shinoda-you.com。",
        );
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试，或直接发送邮件至 contact@shinoda-you.com。");
    }
  };

  return (
    <form
      id="contact-feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8"
    >
      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">在线反馈</h2>
      <p className="text-sm text-foreground-500 mt-2">
        有任何建议、合作意向或想对筱田优说的话，都欢迎留言告诉我们。
      </p>

      <div className="mt-6 flex flex-col gap-5">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-2">
            姓名 <span className="text-accent-500">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            placeholder="请输入您的姓名"
            className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-2">
            邮箱 <span className="text-accent-500">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="请输入您的邮箱地址"
            className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-2">
            留言内容 <span className="text-accent-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={5}
            maxLength={500}
            placeholder="请输入您的留言（最多 500 字）"
            className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
          />
        </div>

        <input
          type="text"
          name="website_alt"
          className="form-hp-field"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <button
          type="submit"
          disabled={status === "submitting"}
          className="h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? "提交中..." : "提交留言"}
        </button>

        {status === "success" && (
          <div className="flex items-start gap-2 p-4 rounded-md bg-primary-100 text-primary-800 text-sm">
            <i className="ri-checkbox-circle-fill mt-0.5" />
            <span>{feedback}</span>
          </div>
        )}
        {status === "error" && (
          <div className="flex items-start gap-2 p-4 rounded-md bg-accent-100 text-accent-800 text-sm">
            <i className="ri-error-warning-fill mt-0.5" />
            <span>{feedback}</span>
          </div>
        )}
      </div>
    </form>
  );
}